<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BroadcastLayout from 'dashboard/components-next/broadcasts/BroadcastLayout.vue';
import StatusBadge from 'dashboard/components-next/broadcasts/StatusBadge.vue';
import BroadcastsAPI from 'dashboard/api/broadcasts';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const broadcastId = computed(() => Number(route.params.broadcastId));

const broadcast = ref(null);
const isFetching = ref(false);
const isSending = ref(false);

const recipients = ref([]);
const recipientsMeta = ref({ page: 1, totalCount: 0 });
const isFetchingRecipients = ref(false);

// Live delivery analytics from the dedicated stats endpoint (accurate mid-send,
// unlike broadcast.stats which is only finalised once sending completes).
const liveStats = ref(null);

const summaryCards = computed(() => {
  const byStatus = liveStats.value?.by_status || {};
  return [
    {
      key: 'total',
      label: t('BROADCASTS.DETAILS.STATS.TOTAL'),
      value: liveStats.value?.total,
    },
    {
      key: 'sent',
      label: t('BROADCASTS.DETAILS.STATS.SENT'),
      value: byStatus.sent,
    },
    {
      key: 'failed',
      label: t('BROADCASTS.DETAILS.STATS.FAILED'),
      value: byStatus.failed,
    },
    {
      key: 'skipped',
      label: t('BROADCASTS.DETAILS.STATS.SKIPPED'),
      value: byStatus.skipped,
    },
    {
      key: 'unsubscribed',
      label: t('BROADCASTS.DETAILS.STATS.UNSUBSCRIBED'),
      value: byStatus.unsubscribed,
    },
    {
      key: 'pending',
      label: t('BROADCASTS.DETAILS.STATS.PENDING'),
      value: byStatus.pending,
    },
  ];
});

const rateCards = computed(() => {
  const rates = liveStats.value?.rates;
  if (!rates) return [];
  return [
    {
      key: 'delivery',
      label: t('BROADCASTS.DETAILS.RATES.DELIVERY'),
      value: rates.delivery,
    },
    {
      key: 'error',
      label: t('BROADCASTS.DETAILS.RATES.ERROR'),
      value: rates.error,
    },
    {
      key: 'unsubscribe',
      label: t('BROADCASTS.DETAILS.RATES.UNSUBSCRIBE'),
      value: rates.unsubscribe,
    },
  ];
});

const channelRows = computed(() => liveStats.value?.by_channel || []);
const recentErrors = computed(() => liveStats.value?.recent_errors || []);

const canEdit = computed(() =>
  ['draft', 'scheduled'].includes(broadcast.value?.status)
);

// «Отправить» — только для черновика или неудавшейся рассылки (повторный запуск).
const canSend = computed(() =>
  ['draft', 'failed'].includes(broadcast.value?.status)
);

const formatValue = value => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') {
    return value
      ? t('BROADCASTS.DETAILS.STATS.YES')
      : t('BROADCASTS.DETAILS.STATS.NO');
  }
  return value;
};

const formatRate = value =>
  value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;

const fetchBroadcast = async () => {
  isFetching.value = true;
  try {
    const { data } = await BroadcastsAPI.show(broadcastId.value);
    broadcast.value = data;
  } catch (error) {
    useAlert(t('BROADCASTS.DETAILS.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const fetchStats = async () => {
  try {
    const { data } = await BroadcastsAPI.stats(broadcastId.value);
    liveStats.value = data;
  } catch (error) {
    liveStats.value = null;
  }
};

const fetchRecipients = async (page = 1) => {
  isFetchingRecipients.value = true;
  try {
    const { data } = await BroadcastsAPI.recipients(broadcastId.value, {
      page,
    });
    recipients.value = data.payload || [];
    recipientsMeta.value = {
      page: Number(data.meta?.page) || 1,
      totalCount: Number(data.meta?.total_count) || 0,
    };
  } catch (error) {
    recipients.value = [];
  } finally {
    isFetchingRecipients.value = false;
  }
};

const EMPTY_VALUE = '—';

const formatSentAt = sentAt => (sentAt ? dynamicTime(sentAt) : EMPTY_VALUE);

const formatVariant = variant => variant || EMPTY_VALUE;

const onRecipientsPageChange = page => fetchRecipients(page);

const handleEdit = () => {
  router.push({
    name: 'broadcasts_edit',
    params: { broadcastId: broadcastId.value },
  });
};

const handleSendNow = async () => {
  // Нативный confirm: запуск рассылки — необратимое действие.
  if (!window.confirm(t('BROADCASTS.DETAILS.SEND_CONFIRM'))) return;

  isSending.value = true;
  try {
    await store.dispatch('broadcasts/sendNow', broadcastId.value);
    useAlert(t('BROADCASTS.DETAILS.SEND_SUCCESS'));
    await fetchBroadcast();
    await Promise.all([fetchRecipients(1), fetchStats()]);
  } catch (error) {
    useAlert(error?.message || t('BROADCASTS.DETAILS.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

onMounted(async () => {
  await fetchBroadcast();
  fetchRecipients(1);
  fetchStats();
});
</script>

<template>
  <BroadcastLayout
    :header-title="broadcast?.title || t('BROADCASTS.LIST.UNTITLED')"
    :back-url="{ name: 'broadcasts_index' }"
    :is-fetching="isFetching"
  >
    <template #actions>
      <Button
        v-if="canEdit"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-pencil"
        :label="t('BROADCASTS.DETAILS.EDIT_BUTTON')"
        @click="handleEdit"
      />
      <Button
        v-if="canSend"
        size="sm"
        icon="i-lucide-send"
        :label="t('BROADCASTS.DETAILS.SEND_BUTTON')"
        :is-loading="isSending"
        :disabled="isSending"
        @click="handleSendNow"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <div class="flex items-center gap-3">
          <StatusBadge v-if="broadcast" :status="broadcast.status" />
          <span v-if="broadcast?.dry_run" class="text-xs text-n-slate-11">
            {{ t('BROADCASTS.DETAILS.DRY_RUN_HINT') }}
          </span>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div
            v-for="card in summaryCards"
            :key="card.key"
            class="flex flex-col gap-1 p-3 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-11">{{ card.label }}</span>
            <span class="text-lg font-medium text-n-slate-12">
              {{ formatValue(card.value) }}
            </span>
          </div>
        </div>

        <div v-if="rateCards.length" class="grid grid-cols-3 gap-3">
          <div
            v-for="card in rateCards"
            :key="card.key"
            class="flex flex-col gap-1 p-3 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-11">{{ card.label }}</span>
            <span class="text-lg font-medium text-n-slate-12">
              {{ formatRate(card.value) }}
            </span>
          </div>
        </div>

        <div v-if="channelRows.length" class="flex flex-col gap-2">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('BROADCASTS.DETAILS.BY_CHANNEL.TITLE') }}
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="border-b border-n-weak text-n-slate-11">
                  <th class="px-3 py-2 font-medium text-start">
                    {{ t('BROADCASTS.DETAILS.BY_CHANNEL.CHANNEL') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-end">
                    {{ t('BROADCASTS.DETAILS.STATS.SENT') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-end">
                    {{ t('BROADCASTS.DETAILS.STATS.FAILED') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-end">
                    {{ t('BROADCASTS.DETAILS.STATS.UNSUBSCRIBED') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-end">
                    {{ t('BROADCASTS.DETAILS.STATS.PENDING') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-end">
                    {{ t('BROADCASTS.DETAILS.STATS.TOTAL') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in channelRows"
                  :key="row.channel"
                  class="border-b border-n-weak last:border-b-0"
                >
                  <td class="px-3 py-2 text-n-slate-12">{{ row.channel }}</td>
                  <td class="px-3 py-2 text-end text-n-slate-11">
                    {{ row.sent }}
                  </td>
                  <td class="px-3 py-2 text-end text-n-slate-11">
                    {{ row.failed }}
                  </td>
                  <td class="px-3 py-2 text-end text-n-slate-11">
                    {{ row.unsubscribed }}
                  </td>
                  <td class="px-3 py-2 text-end text-n-slate-11">
                    {{ row.pending }}
                  </td>
                  <td class="px-3 py-2 text-end text-n-slate-12 font-medium">
                    {{ row.total }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="recentErrors.length" class="flex flex-col gap-2">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('BROADCASTS.DETAILS.ERRORS.TITLE') }}
          </h3>
          <ul class="flex flex-col gap-1 text-sm">
            <li
              v-for="(err, index) in recentErrors"
              :key="index"
              class="flex flex-wrap gap-x-2 px-3 py-2 rounded-lg bg-n-ruby-2 text-n-ruby-11"
            >
              <span class="font-medium">{{ err.channel }}</span>
              <span class="text-n-slate-11">{{ `#${err.contact_id}` }}</span>
              <span>{{ err.error }}</span>
            </li>
          </ul>
        </div>

        <div class="flex flex-col gap-2">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('BROADCASTS.DETAILS.RECIPIENTS.TITLE') }}
          </h3>
          <div
            v-if="isFetchingRecipients"
            class="flex items-center justify-center py-6 text-n-slate-11"
          >
            <Spinner />
          </div>
          <div
            v-else-if="!recipients.length"
            class="py-6 text-sm text-center text-n-slate-11"
          >
            {{ t('BROADCASTS.DETAILS.RECIPIENTS.EMPTY') }}
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="border-b border-n-weak text-n-slate-11">
                  <th class="px-3 py-2 font-medium text-start">
                    {{ t('BROADCASTS.DETAILS.RECIPIENTS.CONTACT') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-start">
                    {{ t('BROADCASTS.DETAILS.RECIPIENTS.CHANNEL') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-start">
                    {{ t('BROADCASTS.DETAILS.RECIPIENTS.VARIANT') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-start">
                    {{ t('BROADCASTS.DETAILS.RECIPIENTS.STATUS') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-start">
                    {{ t('BROADCASTS.DETAILS.RECIPIENTS.SENT_AT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="recipient in recipients"
                  :key="recipient.id"
                  class="border-b border-n-weak last:border-b-0"
                >
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ `#${recipient.contact_id}` }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ recipient.channel }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ formatVariant(recipient.variant) }}
                  </td>
                  <td class="px-3 py-2">
                    <StatusBadge :status="recipient.status" type="recipient" />
                    <span
                      v-if="recipient.error"
                      class="block mt-0.5 text-xs text-n-ruby-11"
                    >
                      {{ recipient.error }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ formatSentAt(recipient.sent_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
            <PaginationFooter
              v-if="recipientsMeta.totalCount > 0"
              :current-page="recipientsMeta.page"
              :total-items="recipientsMeta.totalCount"
              :items-per-page="25"
              class="mt-2"
              @update:current-page="onRecipientsPageChange"
            />
          </div>
        </div>
      </div>
    </template>
  </BroadcastLayout>
</template>
